"use client";

import React, { useState } from "react";
import Image from "next/image";
import { PROJECT_INFO } from "@/types/projectConsts";
import { renderTextWithBreaks } from "@/utils/stripHTML";
import defaultFooterData from "@/data/footerContactData.json";

export type INFO = {
  title?: string;
  description1?: string;
  title2?: string;
  description2?: string;
  title3?: string;
  description3?: string;
  phone?: string;
  mail?: string;
  webUrl?: string;
  fb?: string;
  youtube?: string;
};

export interface FooterContactData {
  tag?: string;
  title?: string;
  description?: string;
  hotlineLabel?: string;
  hotlineValue?: string;
  emailLabel?: string;
  emailValue?: string;
  middleColumn?: {
    developersTitle?: string;
    projectLogo?: {
      name?: string;
      src?: string;
    };
    developers?: Array<{
      role?: string;
      name?: string;
      src?: string;
    }>;
    partnersTitle?: string;
    partners?: Array<{
      name?: string;
      src?: string;
    }>;
  };
  formTitle?: string;
  formSecurityNote?: string;
  formSuccessTitle?: string;
  formSuccessDesc?: string;
  nameLabel?: string;
  namePlaceholder?: string;
  phoneLabel?: string;
  phonePlaceholder?: string;
  needLabel?: string;
  needOptions?: Array<{ label: string; value: string }>;
  formFooterNote?: string;
  submitBtnText?: string;
  submittingBtnText?: string;
}

interface FooterContactProps {
  data?: FooterContactData;
  INFO?: INFO;
  apptOptions?: Array<{ label: string; value: string }>;
}

export default function FooterContact({
  data = defaultFooterData,
  INFO,
  apptOptions,
}: FooterContactProps) {
  const {
    tag = defaultFooterData.tag,
    title = defaultFooterData.title,
    description = defaultFooterData.description,
    hotlineLabel = defaultFooterData.hotlineLabel,
    hotlineValue = defaultFooterData.hotlineValue,
    emailLabel = defaultFooterData.emailLabel,
    emailValue = defaultFooterData.emailValue,
    middleColumn = defaultFooterData.middleColumn,
    formTitle = defaultFooterData.formTitle,
    formSecurityNote = defaultFooterData.formSecurityNote,
    formSuccessTitle = defaultFooterData.formSuccessTitle,
    formSuccessDesc = defaultFooterData.formSuccessDesc,
    nameLabel = defaultFooterData.nameLabel,
    namePlaceholder = defaultFooterData.namePlaceholder,
    phoneLabel = defaultFooterData.phoneLabel,
    phonePlaceholder = defaultFooterData.phonePlaceholder,
    needLabel = defaultFooterData.needLabel,
    needOptions = apptOptions || defaultFooterData.needOptions,
    formFooterNote = defaultFooterData.formFooterNote,
    submitBtnText = defaultFooterData.submitBtnText,
    submittingBtnText = defaultFooterData.submittingBtnText,
  } = data || {};

  // Form State
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [need, setNeed] = useState(needOptions[0]?.value || "1PN Plus");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmitForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName || !phone) return;
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
    }, 1000);
  };

  const actualHotline = PROJECT_INFO.hotline || hotlineValue || "0901 888 888";
  const actualEmail =
    PROJECT_INFO.email || emailValue || "contact@dragonhome.vn";

  return (
    <footer
      id="dang-ky-bao-gia"
      className="relative w-full min-h-screen lg:h-screen lg:max-h-screen flex flex-col justify-between bg-[#F6F3ED] text-[#193B2F] select-none overflow-hidden"
    >
      <style>{`
        @keyframes rippleExpand {
          0% {
            transform: translate(50%, -50%) scale(0.85);
            opacity: 0.2;
          }
          50% {
            transform: translate(50%, -50%) scale(1.08);
            opacity: 0.6;
          }
          100% {
            transform: translate(50%, -50%) scale(0.85);
            opacity: 0.2;
          }
        }
      `}</style>

      {/* ================================================================================= */}
      {/* BOTTOM CONTACT & FORM SECTION (WITH LINEAR GRADIENT & MODERN EFFECTS) */}
      {/* ================================================================================= */}
      <div className="w-full flex-1 min-h-0 bg-[#0D291E] text-white px-6 sm:px-10 lg:px-14 xl:px-16 py-12 sm:py-16 lg:py-12 flex flex-col justify-center relative z-20 overflow-hidden">
        {/* BACKGROUND GRADIENT IN TAILWIND PRIMARY & PRIMARY DARK PALETTE */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-[#085C47] to-primaryDark pointer-events-none z-0 opacity-95" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/25 rounded-full blur-3xl pointer-events-none z-0" />

        {/* CONCENTRIC EXPANDING RIPPLE CIRCLES AT TOP RIGHT */}
        <div className="absolute top-0 right-0 w-0 h-0 pointer-events-none z-[1]">
          <div
            className="absolute top-0 right-0 w-[900px] h-[900px] rounded-full border border-white/20"
            style={{ animation: "rippleExpand 7s ease-in-out infinite" }}
          />
          <div
            className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full border border-white/25"
            style={{ animation: "rippleExpand 6s ease-in-out infinite 1s" }}
          />
          <div
            className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full border border-[#C9AB81]/40 bg-[#C9AB81]/10"
            style={{ animation: "rippleExpand 5s ease-in-out infinite 0.5s" }}
          />
        </div>

        {/* LEFT DECORATIVE SHADOW OVERLAY IMAGE */}
        <div className="absolute inset-y-0 left-0 w-full sm:w-[65%] lg:w-[55%] h-full pointer-events-none z-[1] opacity-100 mix-blend-overlay">
          <Image
            src="/images/deco_shadow_left_bottom.png"
            alt="Decorative Shadow Overlay"
            fill
            sizes="(max-width: 1024px) 100vw, 55vw"
            priority
            className="object-cover object-left-bottom"
          />
        </div>

        <div className="relative z-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start lg:items-stretch my-auto">
          {/* ================= COL 1: LEFT BRAND INFO & HOTLINE (~4 COLUMNS) ================= */}
          <div className="lg:col-span-4 h-full flex flex-col justify-between space-y-6 sm:space-y-7">
            <div className="space-y-6 sm:space-y-7">
              <h2
                className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-lora font-normal text-white tracking-tight title-leading"
                style={{ lineHeight: 1.15 }}
              >
                {renderTextWithBreaks(title)}
              </h2>

              <div className="flex items-center gap-3">
                <span className="w-7 h-[1px] bg-[#C9AB81]" />
                <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] text-[#C9AB81] font-sans">
                  {tag}
                </span>
              </div>

              <p className="text-xs sm:text-sm font-sans font-light text-white/75 leading-relaxed max-w-md">
                {renderTextWithBreaks(description)}
              </p>
            </div>

            {/* HOTLINE & EMAIL */}
            <div className="grid grid-cols-2 gap-4 pt-4 sm:pt-6 border-t border-white/10 text-xs sm:text-sm mt-auto">
              <div>
                <span className="text-white/50 text-xs uppercase font-bold block mb-1 tracking-wider">
                  {hotlineLabel}
                </span>
                <span className="font-sans font-bold text-xs sm:text-sm md:text-base text-white break-words block">
                  {actualHotline}
                </span>
              </div>

              <div>
                <span className="text-white/50 text-xs uppercase font-bold block mb-1 tracking-wider">
                  {emailLabel}
                </span>
                <span className="font-sans font-bold text-xs sm:text-sm md:text-base text-white break-words block">
                  {actualEmail}
                </span>
              </div>
            </div>
          </div>

          {/* ================= COL 2: MIDDLE LOGOS COLUMN (PROJECT, DEVELOPERS & PARTNERS - ~4 COLUMNS) ================= */}
          <div className="lg:col-span-4 h-full flex flex-col justify-between space-y-5 lg:px-4 border-t lg:border-t-0 lg:border-l border-white/10 pt-6 lg:pt-0">
            {/* TOP ROW: PROJECT LOGO (45% ZONE) + DEVELOPERS LOGOS (55% ZONE) */}
            <div className="space-y-2">
              <span className="text-white/50 text-xs uppercase font-bold tracking-widest block text-center lg:text-left">
                {middleColumn?.developersTitle || "DỰ ÁN & CHỦ ĐẦU TƯ"}
              </span>
              <div className="flex items-center justify-between p-3.5 sm:p-4 bg-white text-primaryDark rounded-sm border border-white/20 select-none min-h-[120px] sm:min-h-[140px] lg:min-h-[150px] w-full shadow-sm">
                {/* Project Logo Zone (45% Width, Centered & Larger) */}
                {middleColumn?.projectLogo && (
                  <div className="w-[45%] flex flex-col items-center justify-center gap-1 text-center shrink-0 px-1">
                    <div className="relative h-20 sm:h-24 md:h-28 lg:h-32 w-full max-w-[200px] sm:max-w-[260px] md:max-w-[300px] flex items-center justify-center p-2">
                      {middleColumn.projectLogo.src ? (
                        <Image
                          src={middleColumn.projectLogo.src}
                          alt={middleColumn.projectLogo.name || "Project Logo"}
                          fill
                          sizes="auto"
                          className="object-contain"
                        />
                      ) : null}
                    </div>
                    {/* {middleColumn.projectLogo.name && (
                      <span className="text-[9px] sm:text-[9.5px] font-sans font-medium text-primaryDark/80 uppercase tracking-wide truncate max-w-full">
                        {middleColumn.projectLogo.name}
                      </span>
                    )} */}
                  </div>
                )}

                {/* Vertical Divider */}
                <div className="w-[1px] h-20 sm:h-24 md:h-28 bg-primaryDark/15 shrink-0" />

                {/* Developers Zone (55% Width, Centered) */}
                {middleColumn?.developers && (
                  <div className="w-[55%] flex items-center justify-center gap-2 sm:gap-4 shrink-0 px-1">
                    {middleColumn.developers.map((dev: any, idx: number) => (
                      <div
                        key={idx}
                        className="flex flex-col items-center justify-center gap-1 shrink-0 text-center"
                      >
                        <div className="relative h-10 sm:h-12 md:h-14 w-16 sm:w-20 md:w-24 flex items-center justify-center p-1">
                          {dev.src ? (
                            <Image
                              src={dev.src}
                              alt={dev.name || dev.role || "Developer"}
                              fill
                              sizes="(max-width: 768px) 64px, 96px"
                              className="object-contain"
                            />
                          ) : null}
                        </div>
                        <span className="text-[8.5px] sm:text-[9px] font-sans font-medium text-primaryDark/80 uppercase tracking-wide truncate max-w-full">
                          {dev.role || dev.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* BOTTOM GRID: STRATEGIC PARTNERS LOGOS WITH NAMES */}
            {middleColumn?.partners && middleColumn.partners.length > 0 && (
              <div className="space-y-2 pt-3 border-t border-white/10 mt-auto">
                <span className="text-white/50 text-xs uppercase font-bold tracking-widest block">
                  {middleColumn.partnersTitle || "ĐỐI TÁC CHIẾN LƯỢC"}
                </span>
                <div className="grid grid-cols-2 gap-2.5">
                  {middleColumn.partners.map((partner: any, idx: number) => (
                    <div
                      key={idx}
                      className="flex flex-col items-center justify-center p-2 bg-white text-primaryDark border border-white/10 rounded-sm hover:bg-white/95 transition-colors text-center select-none min-h-[70px] sm:min-h-[76px] shadow-sm"
                    >
                      {partner.src ? (
                        <div className="relative h-9 sm:h-10 w-full mb-1 flex items-center justify-center p-1">
                          <Image
                            src={partner.src}
                            alt={partner.name || `Partner ${idx + 1}`}
                            fill
                            sizes="(max-width: 768px) 120px, 200px"
                            className="object-contain p-0.5"
                          />
                        </div>
                      ) : null}
                      <span className="text-[9px] sm:text-[9.5px] font-sans font-medium text-primaryDark/80 line-clamp-1 px-1">
                        {partner.name || `Đối tác ${idx + 1}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ================= COL 3: RIGHT CONTACT FORM (~4 COLUMNS) ================= */}
          <div className="lg:col-span-4 h-full flex flex-col justify-between space-y-6 sm:space-y-7 lg:pl-4 border-t lg:border-t-0 lg:border-l border-white/10 pt-6 lg:pt-0">
            <div className="space-y-2 pb-1 border-b border-white/10">
              <h2
                className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-lora font-normal text-white tracking-tight title-leading"
                style={{ lineHeight: 1.15 }}
              >
                {renderTextWithBreaks(formTitle)}
              </h2>
              <span className="text-[9.5px] sm:text-[10px] font-sans font-medium text-white/40 uppercase tracking-widest block">
                {formSecurityNote}
              </span>
            </div>

            {submitSuccess ? (
              <div className="p-6 border border-[#C9AB81]/40 bg-[#C9AB81]/10 text-center space-y-2 rounded-sm">
                <h4 className="font-lora text-lg font-normal text-white">
                  {formSuccessTitle}
                </h4>
                <p className="text-xs sm:text-sm font-sans text-white/70">
                  {formSuccessDesc}
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmitForm}
                className="space-y-5 sm:space-y-6"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <label className="block text-[10px] sm:text-[10.5px] font-sans font-bold tracking-widest text-white/70 uppercase">
                      {nameLabel}
                    </label>
                    <input
                      type="text"
                      required
                      placeholder={namePlaceholder}
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full bg-transparent border-b border-white/20 py-2.5 sm:py-3 text-xs sm:text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#C9AB81] transition-colors"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-[10px] sm:text-[10.5px] font-sans font-bold tracking-widest text-white/70 uppercase">
                      {phoneLabel}
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder={phonePlaceholder}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full bg-transparent border-b border-white/20 py-2.5 sm:py-3 text-xs sm:text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#C9AB81] transition-colors"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[10px] sm:text-[10.5px] font-sans font-bold tracking-widest text-white/70 uppercase">
                    {needLabel}
                  </label>
                  <select
                    value={need}
                    onChange={(e) => setNeed(e.target.value)}
                    className="w-full bg-[#0A1B24] border-b border-white/20 py-2.5 sm:py-3 text-xs sm:text-sm text-white focus:outline-none focus:border-[#C9AB81] transition-colors cursor-pointer"
                  >
                    {needOptions.map((opt, i) => (
                      <option
                        key={i}
                        value={opt.value}
                        className="bg-[#0A1B24] text-white"
                      >
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="pt-3 sm:pt-4 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="text-[9.5px] sm:text-[10px] font-sans font-light text-white/50 leading-relaxed max-w-xs">
                    {formFooterNote}
                  </p>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-3 px-6 sm:px-7 py-3 rounded-full border border-white/40 bg-transparent text-xs font-sans font-bold tracking-[0.18em] text-white uppercase hover:bg-white/10 hover:border-white transition-all cursor-pointer focus:outline-none shrink-0 group self-end sm:self-auto"
                  >
                    <span>
                      {isSubmitting ? submittingBtnText : submitBtnText}
                    </span>
                    <div className="w-5 h-5 rounded-full bg-[#E5D4B2] text-[#0A1B24] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <Image
                        src="/icons/arrow_outward.svg"
                        alt="arrow"
                        width={9}
                        height={9}
                        className="w-2.5 h-2.5 object-contain"
                      />
                    </div>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
